'use client';
import React, { useState } from 'react';
import { Plus, X, Info } from 'lucide-react';
import { ParsedProfile } from './OnboardingWizard';

interface Step2ReviewProps {
  profile: ParsedProfile;
  onChange: (p: ParsedProfile) => void;
  onNext: () => void;
  onBack: () => void;
}

function TagEditor({ tags, onChange, placeholder }: { tags: string[]; onChange: (t: string[]) => void; placeholder: string }) {
  const [input, setInput] = useState('');

  const add = () => {
    const trimmed = input.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput('');
  };

  const remove = (tag: string) => onChange(tags.filter((t) => t !== tag));

  return (
    <div className="flex flex-wrap gap-2 p-2.5 rounded-lg bg-input border border-border focus-within:border-primary/50 min-h-[44px]">
      {tags.map((tag) => (
        <span key={`tag-editor-${tag}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-mono-data">
          {tag}
          <button type="button" onClick={() => remove(tag)} className="hover:text-red-400 transition-colors" aria-label={`Remove ${tag}`}>
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder={placeholder}
        className="flex-1 min-w-[120px] bg-transparent text-xs text-foreground placeholder-muted-foreground outline-none"
        aria-label={placeholder}
      />
      <button type="button" onClick={add} className="text-muted-foreground hover:text-primary transition-colors" aria-label="Add tag">
        <Plus size={14} />
      </button>
    </div>
  );
}

function StrengthEditor({ strengths, onChange }: { strengths: string[]; onChange: (s: string[]) => void }) {
  const update = (index: number, value: string) => {
    const updated = [...strengths];
    updated[index] = value;
    onChange(updated);
  };

  return (
    <div className="flex flex-col gap-2">
      {strengths.map((s, i) => (
        <div key={`strength-${i}`} className="flex items-center gap-2">
          <span className="font-mono-data text-xs text-muted-foreground w-5 text-right flex-shrink-0">{i + 1}.</span>
          <input
            value={s}
            onChange={(e) => update(i, e.target.value)}
            className="flex-1 bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors"
            placeholder={`Strength ${i + 1}`}
            aria-label={`Strength ${i + 1}`}
          />
        </div>
      ))}
    </div>
  );
}

export default function Step2Review({ profile, onChange, onNext, onBack }: Step2ReviewProps) {
  const update = <K extends keyof ParsedProfile>(key: K, value: ParsedProfile[K]) => {
    onChange({ ...profile, [key]: value });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="surface-elevated p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Info size={14} className="text-primary" />
          </div>
          <div>
            <h2 className="font-heading text-xl text-foreground mb-1">Review AI-Generated Profile</h2>
            <p className="text-sm text-muted-foreground">
              All fields below are fully editable. Review for accuracy — no PII should appear anywhere.
              Your pseudonym is permanent and cannot be changed.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
          <span className="text-xs text-muted-foreground font-mono-data">Your pseudonym:</span>
          <span className="font-mono-data text-primary font-bold text-lg">{profile.pseudonym}</span>
          <span className="text-xs text-muted-foreground">— permanent, cannot be changed</span>
        </div>
      </div>

      <div className="surface-elevated p-6 flex flex-col gap-5">
        <h3 className="font-heading text-base text-foreground border-b border-border pb-3">Public Profile Fields</h3>

        <div>
          <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5" htmlFor="headline">
            Headline
          </label>
          <p className="text-xs text-muted-foreground mb-2">One sentence. No names, employers, or specific locations.</p>
          <textarea
            id="headline"
            value={profile.headline}
            onChange={(e) => update('headline', e.target.value)}
            rows={2}
            className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors resize-none"
            placeholder="One sentence summary, no PII"
          />
        </div>

        <div>
          <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5" htmlFor="summaryBio">
            Summary Bio
          </label>
          <p className="text-xs text-muted-foreground mb-2">Max 150 words. Third person. Zero PII.</p>
          <textarea
            id="summaryBio"
            value={profile.summaryBio}
            onChange={(e) => update('summaryBio', e.target.value)}
            rows={6}
            className="w-full bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors resize-none"
            placeholder="Third person bio, no PII"
          />
          <p className="text-xs text-muted-foreground mt-1 text-right font-mono-data">
            {profile.summaryBio.split(/\s+/).filter(Boolean).length}/150 words
          </p>
        </div>

        <div>
          <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5">
            Domain Tags
          </label>
          <p className="text-xs text-muted-foreground mb-2">3–6 short keywords. Press Enter to add.</p>
          <TagEditor tags={profile.domainTags} onChange={(t) => update('domainTags', t)} placeholder="Add domain tag…" />
        </div>

        <div>
          <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5">
            Sector Exposure
          </label>
          <p className="text-xs text-muted-foreground mb-2">Industries only — no company names.</p>
          <TagEditor tags={profile.sectorExposure} onChange={(t) => update('sectorExposure', t)} placeholder="Add sector…" />
        </div>

        <div>
          <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5">
            Experience Tier
          </label>
          <div className="flex gap-3">
            {(['SENIOR', 'PRINCIPAL', 'EXPERT'] as const).map((tier) => (
              <button
                key={`tier-${tier}`}
                type="button"
                onClick={() => update('experienceTier', tier)}
                className={`flex-1 py-2.5 px-3 rounded-lg border text-xs font-mono-data font-medium transition-all duration-150 ${
                  profile.experienceTier === tier
                    ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/30'
                }`}
              >
                {tier}
                <div className="text-[9px] font-normal mt-0.5 opacity-70">
                  {tier === 'SENIOR' ? '8–14 yrs' : tier === 'PRINCIPAL' ? '15–24 yrs' : '25+ yrs'}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5">
            Key Strengths
          </label>
          <p className="text-xs text-muted-foreground mb-2">Exactly 5 strength statements. No PII.</p>
          <StrengthEditor strengths={profile.keyStrengths} onChange={(s) => update('keyStrengths', s)} />
        </div>

        <div>
          <label className="block text-xs font-mono-data text-muted-foreground uppercase tracking-wider mb-1.5">
            Engagement Types
          </label>
          <p className="text-xs text-muted-foreground mb-2">What formats can you deliver?</p>
          <TagEditor tags={profile.engagementTypes} onChange={(t) => update('engagementTypes', t)} placeholder="Add engagement type…" />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3 px-6 rounded-lg border border-border text-muted-foreground font-medium text-sm hover:border-primary/30 hover:text-foreground active:scale-95 transition-all duration-150"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-3 px-6 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 active:scale-95 transition-all duration-150"
        >
          Continue to Rates
        </button>
      </div>
    </div>
  );
}